import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { User, UserModel } from '@shared/models/user';
import { environment } from '@env/environment';
import { Observable, map } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { ApiClientConfig } from '../api-client.config';
import {   ModerationAreas,
  ModerationAreasResponse,
  ModeratorRole,
  ModeratorRoleResponse,
  UserFrameResponse,
  UserInfoResponse,
  UserManagementResponse
 } from './api.user.model';

export class UserApi {
  private apiUrl: string = environment.BASE_PATH + '/user';

  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getUserById(id: string): Observable<UserInfoResponse> {
    return this.http.get<any>(this.apiUrl + '/users/' + id).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        throw error.error;
      })
    );
  }

  getUserInfo(userId: string): Observable<any> {
    return this.http.get<any>(this.apiUrl + '/users/info/' + userId).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        throw error.error;
      })
    );
  }

  getTotalsUser(roles: any, statuses: any, keyword: string) {
    return this.http.get<UserModel[]>(this.apiUrl + '/users').pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        const users = res.data;
        res.data = users.filter((item: any) => {
          if (keyword.trim() !== '') {
            return (
              (item.last_name?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.first_name
                  ?.toLowerCase()
                  .includes(keyword.toLowerCase()) ||
                item.email?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.register_date
                  ?.toLowerCase()
                  .includes(keyword.toLowerCase())) &&
              (roles && roles.length > 0
                ? roles.some((element: any) => item.roles.includes(element))
                : true) &&
              (statuses && statuses.length > 0
                ? statuses.includes(item.status)
                : true)
            );
          } else {
            return (
              (roles && roles.length > 0
                ? roles.some((element: any) => item.roles.includes(element))
                : true) &&
              (statuses && statuses.length > 0
                ? statuses.includes(item.status)
                : true)
            );
          }
        });
        return res.data;
      }),
      catchError((error) => {
        throw error.error;
      })
    );
  }

  updateUser(userId: string, userDetail: Partial<any>): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + '/users/' + userId, userDetail)
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res.data;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }

  delete(id: string) {
    return this.http.delete<User>(this.apiUrl + '/users/' + id).pipe(
      timeout(this.config.responseTimeout),
      catchError((error) => {
        throw error.error;
      })
    );
  }

  getUserManagement(
    pageNum: number,
    pageSize: number,
    status: string
  ): Observable<UserManagementResponse> {
    return this.http
      .get<UserManagementResponse>(
        this.apiUrl +
          `/admin/search?pageNum=${pageNum}&pageSize=${pageSize}&status=${status}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: UserManagementResponse) => {
          return res;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }

  getToken(): string | null {
    const auth = localStorage.getItem(environment.TOKEN_KEY);
    const authValue = auth ?? '';
    if (authValue !== '') {
      try {
        return JSON.parse(authValue).accessToken; // Parse the JSON string to an object
      } catch (error) {
        console.error('Error parsing authentication token:', error);
        return null;
      }
    } else {
      return null;
    }
  }

  getModeratorRole(): Observable<ModeratorRole[]> {
    const token = this.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    return this.http
      .get<ModeratorRoleResponse>(`${this.apiUrl}/admin/roles/module`, {
        headers,
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map((res) => {
          return res.data;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }

  getModerationAreas(textSearch: string): Observable<ModerationAreas[]> {
    const params = new HttpParams();
    if (textSearch) {
      params.set('textSearch', textSearch);
    }
    return this.http
      .get<ModerationAreasResponse>(`${this.apiUrl}/admin/moderation-areas`, {
        params,
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map((res) => {
          return res.data;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }

  getTokenToLoadIframe(): Observable<string> {
    return this.http
      .get<UserFrameResponse>(`${this.apiUrl}/admin/approval-token`)
      .pipe(
        timeout(this.config.responseTimeout),
        map((res) => {
          return res.data;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }

  getPermissionCodeList() {
    return this.http
      .get<any>(
        `${this.apiUrl}/admin-permissions/hub/FLIGHT_MODULE_ADMIN/permission`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }
  getPermissionCodeByModule(moduleType: any) {
    return this.http
      .get<any>(`${this.apiUrl}/admin-permissions/hub/${moduleType}/permission`)
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }

  getAllUserAdmin(pageSize: number, pageNum: number, textSearch: string, ): Observable<any> {
    const query = {pageNum:  pageNum, pageSize: pageSize, textSearch: textSearch };
    
    return this.http
      .get<any>(`${this.apiUrl}/admin/search`, {
        params: query,
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map((res) => {
          return res;
        }),
        catchError((error) => {
          throw error.error;
        })
      );
  }
}

